import React, { useState, useEffect, useContext } from "react";
import { Table, Spinner, Button, Modal, Form, Alert, Badge } from "react-bootstrap";
import { FiEye } from "react-icons/fi";
import { FaWallet, FaFileInvoice, FaMoneyBillWave, FaCalendarAlt, FaShoppingCart } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import { TranslatorContext } from "../../../../context/Translator";

const SalesTab = ({ sales, loading, customerId, onRefresh }) => {
    const navigate = useNavigate();
    const { t } = useContext(TranslatorContext);

    // Customer balance and pending sales states
    const [customerBalance, setCustomerBalance] = useState(null);
    const [showPendingSales, setShowPendingSales] = useState(false);
    const [pendingSales, setPendingSales] = useState([]);
    const [loadingBalance, setLoadingBalance] = useState(false);
    const [showCustomerPaymentModal, setShowCustomerPaymentModal] = useState(false);
    const [selectedSaleForPayment, setSelectedSaleForPayment] = useState(null);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [customerPaymentForm, setCustomerPaymentForm] = useState({
        payment_method_id: '',
        amount: 0,
        payment_date: getTodayDate()
    });

    // Get today's date in YYYY-MM-DD format
    function getTodayDate() {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    const handleViewSale = (saleId) => {
        navigate(`/sale-edit/${saleId}`);
    };

    // Fetch customer balance when customerId is provided
    useEffect(() => {
        if (customerId) {
            fetchCustomerBalance(customerId);
            fetchPaymentMethods();
        } else {
            setCustomerBalance(null);
            setPendingSales([]);
            setShowPendingSales(false);
        }
    }, [customerId]);

    const fetchCustomerBalance = async (custId) => {
        setLoadingBalance(true);
        try {
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/customer-balance/${custId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );
            setCustomerBalance(response.data);
        } catch (error) {
            console.error("Error fetching customer balance:", error);
            setCustomerBalance(null);
        } finally {
            setLoadingBalance(false);
        }
    };

    const fetchPendingSales = async (custId) => {
        setLoadingBalance(true);
        try {
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/customer-pending-sales/${custId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );
            if (response.data.status) {
                setPendingSales(response.data.pending_sales || []);
            }
        } catch (error) {
            console.error("Error fetching pending sales:", error);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: t('Failed to fetch pending sales'),
                showConfirmButton: false,
                timer: 2000
            });
        } finally {
            setLoadingBalance(false);
        }
    };

    const fetchPaymentMethods = async () => {
        try {
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/payment-methods`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                if (Array.isArray(response.data)) {
                    setPaymentMethods(response.data);
                } else if (response.data.paymentMethods && Array.isArray(response.data.paymentMethods)) {
                    setPaymentMethods(response.data.paymentMethods);
                } else if (response.data.data && Array.isArray(response.data.data)) {
                    setPaymentMethods(response.data.data);
                } else {
                    setPaymentMethods([]);
                }
            }
        } catch (error) {
            console.error("Error fetching payment methods:", error);
            setPaymentMethods([]);
        }
    };

    const handleShowPendingSales = async () => {
        if (!customerId) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t('No customer selected'),
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }

        if (!showPendingSales) {
            await fetchPendingSales(customerId);
            setShowPendingSales(true);
        } else {
            setShowPendingSales(false);
        }
    };

    const handleMakeCustomerPayment = (sale) => {
        setSelectedSaleForPayment(sale);
        setCustomerPaymentForm({
            payment_method_id: '',
            amount: sale.due,
            payment_date: getTodayDate()
        });
        setShowCustomerPaymentModal(true);
    };

    // Refresh all data after payment
    const refreshAllData = async () => {
        if (customerId) {
            // Refresh customer balance
            await fetchCustomerBalance(customerId);

            // If pending sales modal is open, refresh pending sales
            if (showPendingSales) {
                await fetchPendingSales(customerId);
            }

            // Call parent refresh to update the main sales list
            if (onRefresh && typeof onRefresh === 'function') {
                await onRefresh();
            }
        }
    };

    const handleCustomerPaymentSubmit = async () => {
        if (!customerPaymentForm.payment_method_id) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t('Please select payment method'),
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }

        if (customerPaymentForm.amount <= 0) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t('Amount must be greater than 0'),
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }

        setSubmitting(true);
        try {
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/sale-payment`,
                {
                    customer_id: customerId,
                    amount: customerPaymentForm.amount,
                    payment_method_id: customerPaymentForm.payment_method_id,
                    payment_date: customerPaymentForm.payment_date
                },
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.status) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: t('Payment allocated successfully'),
                    showConfirmButton: false,
                    timer: 2000
                });

                setShowCustomerPaymentModal(false);

                // IMPORTANT: Refresh ALL data after successful payment
                await refreshAllData();

                if (response.data.remaining_unallocated > 0) {
                    Swal.fire({
                        toast: true,
                        position: 'top-end',
                        icon: 'info',
                        title: `${t('Remaining amount')}: ${response.data.remaining_unallocated}`,
                        showConfirmButton: false,
                        timer: 3000
                    });
                }
            }
        } catch (error) {
            console.error("Error making payment:", error);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: error.response?.data?.message || t('Payment failed'),
                showConfirmButton: false,
                timer: 3000
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Loading sales...</p>
            </div>
        );
    }

    return (
        <>
            {/* Customer Balance Display - Professional Card */}
            {customerBalance && customerBalance.total_balance > 0 && (
                <div className="mb-4" style={{
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                    borderRadius: '16px',
                    padding: '24px',
                    boxShadow: '0 8px 32px rgba(15, 52, 96, 0.35)',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Background decorative circles */}
                    <div style={{
                        position: 'absolute', top: '-40px', right: '-40px',
                        width: '150px', height: '150px', borderRadius: '50%',
                        background: 'rgba(255,255,255,0.04)', pointerEvents: 'none'
                    }} />
                    <div style={{
                        position: 'absolute', bottom: '-20px', left: '30%',
                        width: '100px', height: '100px', borderRadius: '50%',
                        background: 'rgba(255,255,255,0.03)', pointerEvents: 'none'
                    }} />

                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        {/* Left: Balance info */}
                        <div className="d-flex align-items-center gap-3">
                            <div style={{
                                background: 'rgba(255,193,7,0.15)',
                                border: '1px solid rgba(255,193,7,0.3)',
                                borderRadius: '12px',
                                width: '52px', height: '52px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <FaWallet size={22} color="#ffc107" />
                            </div>
                            <div>
                                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: 500, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '2px' }}>
                                    {t('Customer Balance')}
                                </div>
                                <div style={{ color: '#ffffff', fontSize: '26px', fontWeight: 700, lineHeight: 1.1 }}>
                                    {parseFloat(customerBalance.total_balance).toLocaleString()}
                                </div>
                            </div>
                        </div>

                        {/* Middle: Stats chips */}
                        <div className="d-flex gap-2 flex-wrap">
                            {customerBalance.total_due != null && (
                                <div style={{
                                    background: 'rgba(255,255,255,0.08)',
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    borderRadius: '10px', padding: '8px 16px', textAlign: 'center'
                                }}>
                                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', marginBottom: '2px' }}>{t('Total Due')}</div>
                                    <div style={{ color: '#ffc107', fontWeight: 700, fontSize: '18px' }}>
                                        {parseFloat(customerBalance.total_balance).toLocaleString()}
                                    </div>
                                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>{t('outstanding')}</div>
                                </div>
                            )}
                        </div>

                        {/* Right: Action button */}

                        <Button
                            onClick={handleShowPendingSales}
                            style={{
                                background: 'linear-gradient(135deg, #ffc107, #ff9800)',
                                border: 'none',
                                borderRadius: '10px',
                                padding: '10px 20px',
                                fontWeight: 600,
                                fontSize: '13px',
                                color: '#1a1a2e',
                                boxShadow: '0 4px 15px rgba(255,193,7,0.3)',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <FaFileInvoice className="me-2" size={14} />
                            {t('View Pending Sales')}
                        </Button>

                    </div>
                </div>
            )}

            {/* Sales Table */}
            {sales.length === 0 ? (
                <div className="text-center py-5">
                    <p className="text-muted">No sales found for this customer.</p>
                </div>
            ) : (
                <div className="table-responsive">
                    <Table className="mc-table" style={{ border: 'none' }}>
                        <thead style={{ borderBottom: '2px solid #dee2e6' }}>
                            <tr>
                                <th style={{ border: 'none' }}>Sale #</th>
                                <th style={{ border: 'none' }}>Sale Date</th>
                                <th style={{ border: 'none' }}>Entered By</th>
                                <th style={{ border: 'none' }}>Products</th>
                                <th style={{ border: 'none' }}>Amount</th>
                                <th style={{ border: 'none' }}>Status</th>
                                <th style={{ border: 'none' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sales.map((sale) => (
                                <tr key={sale.id}>
                                    <td style={{ border: 'none' }}>
                                        <span className="fw-bold">
                                            {sale.sale_number || `SALE-${sale.id}`}
                                        </span>
                                    </td>
                                    <td style={{ border: 'none' }}>
                                        {formatDate(sale.sale_date)}
                                    </td>
                                    <td style={{ border: 'none' }}>
                                        {sale.entered_by || '-'}
                                    </td>
                                    <td style={{ border: 'none' }}>
                                        <div className="d-flex flex-wrap gap-1">
                                            {sale.sale_summary?.slice(0, 3).map((item, i) => (
                                                <span key={i} className="badge bg-light text-dark">
                                                    {item.name} × {item.qty}
                                                </span>
                                            ))}
                                            {sale.sale_summary?.length > 3 && (
                                                <span className="badge bg-secondary">
                                                    +{sale.sale_summary.length - 3} more
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td style={{ border: 'none' }}>
                                        {sale.total_amount ? parseFloat(sale.total_amount).toLocaleString() : '-'}
                                    </td>

                                    <td style={{ border: 'none' }}>
                                        <span
                                            className={`badge bg-${sale.status === 'paid' ? 'success' :
                                                sale.status === 'partial' ? 'warning' :
                                                    'danger'
                                                }`}
                                            style={sale.status === 'partial' ? { color: '#000' } : {}}
                                        >
                                            {sale.status || '-'}
                                        </span>
                                    </td>
                                    <td style={{ border: 'none' }}>
                                        <Button
                                            variant="link"
                                            className="p-0 text-primary"
                                            onClick={() => handleViewSale(sale.id)}
                                            style={{ textDecoration: 'none' }}
                                        >
                                            <FiEye size={18} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}

            {/* Customer Payment Modal for Pending Sales */}
            <Modal show={showCustomerPaymentModal} onHide={() => setShowCustomerPaymentModal(false)} size="lg">
                <Modal.Header closeButton className="p-3">
                    <Modal.Title>{t('Make Payment')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedSaleForPayment && (
                        <>
                            <div className="mb-3">
                                <strong>{t('Sale #')}:</strong> {selectedSaleForPayment.sale_number}<br />
                                <strong>{t('Sale Date')}:</strong> {formatDate(selectedSaleForPayment.sale_date)}<br />
                                <strong>{t('Total Amount')}:</strong> {parseFloat(selectedSaleForPayment.total).toLocaleString()}<br />
                                <strong>{t('Paid Amount')}:</strong> {parseFloat(selectedSaleForPayment.paid).toLocaleString()}<br />
                                <strong>{t('Due Amount')}:</strong> <span className="text-danger fw-bold">{parseFloat(selectedSaleForPayment.due).toLocaleString()}</span>
                            </div>

                            {/* Products List */}
                            {selectedSaleForPayment.products && selectedSaleForPayment.products.length > 0 && (
                                <div className="mb-3">
                                    <strong>{t('Products')}:</strong>
                                    <div className="mt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                        <table className="table table-sm table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>{t('Product')}</th>
                                                    <th>{t('Qty')}</th>
                                                    <th>{t('Price')}</th>
                                                    <th>{t('Discount')}</th>
                                                    <th>{t('Extra')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedSaleForPayment.products.map((product, idx) => (
                                                    <tr key={idx}>
                                                        <td>{product.product_name}</td>
                                                        <td>{product.quantity}</td>
                                                        <td>{parseFloat(product.sale_price).toLocaleString()}</td>
                                                        <td>{parseFloat(product.discount_price).toLocaleString()}</td>
                                                        <td>{parseFloat(product.extra_price).toLocaleString()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label>{t('Payment Method')} <span className="text-danger">*</span></Form.Label>
                                <select
                                    className="form-control"
                                    value={customerPaymentForm.payment_method_id}
                                    onChange={(e) => setCustomerPaymentForm({ ...customerPaymentForm, payment_method_id: e.target.value })}
                                >
                                    <option value="">{t('Select Payment Method')}</option>
                                    {paymentMethods.map(pm => (
                                        <option key={pm.id} value={pm.id}>{pm.name}</option>
                                    ))}
                                </select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Amount')} <span className="text-danger">*</span></Form.Label>
                                <input
                                    type="number"
                                    className="form-control"
                                    step="0.01"
                                    min="0.01"
                                    max={selectedSaleForPayment.due}
                                    placeholder="Enter amount"
                                    value={customerPaymentForm.amount}
                                    onChange={(e) =>
                                        setCustomerPaymentForm({
                                            ...customerPaymentForm,
                                            amount: e.target.value
                                        })
                                    }
                                />
                                <small className="text-muted">{t('Max amount')}: {parseFloat(selectedSaleForPayment.due).toLocaleString()}</small>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Payment Date')} <span className="text-danger">*</span></Form.Label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={customerPaymentForm.payment_date}
                                    onChange={(e) => setCustomerPaymentForm({ ...customerPaymentForm, payment_date: e.target.value })}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="p-3">
                    <Button variant="secondary" onClick={() => setShowCustomerPaymentModal(false)}>
                        {t('Cancel')}
                    </Button>
                    <Button variant="primary" onClick={handleCustomerPaymentSubmit} disabled={submitting}>
                        {submitting ? <Spinner animation="border" size="sm" /> : t('Pay Now')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Pending Sales Modal */}
            <Modal
                show={showPendingSales}
                onHide={() => setShowPendingSales(false)}
                size="xl"
                centered
            >
                <Modal.Header closeButton style={{
                    background: 'linear-gradient(135deg, #1a1a2e, #0f3460)',
                    borderBottom: 'none',
                    padding: '20px 24px'
                }}>
                    <Modal.Title style={{ color: '#fff', fontSize: '16px', fontWeight: 600 }}>
                        <FaFileInvoice className="me-2" style={{ color: '#ffc107' }} />
                        {t('Pending Sales')}
                        {pendingSales.length > 0 && (
                            <Badge style={{
                                background: 'rgba(255,193,7,0.2)',
                                color: '#ffc107',
                                border: '1px solid rgba(255,193,7,0.3)',
                                marginLeft: '10px',
                                fontWeight: 600,
                                fontSize: '12px',
                                padding: '4px 10px',
                                borderRadius: '20px'
                            }}>
                                {pendingSales.length} {t('pending')}
                            </Badge>
                        )}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ padding: '0', background: '#f8f9fa' }}>
                    {loadingBalance ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2 text-muted">{t('Loading...')}</p>
                        </div>
                    ) : pendingSales.length === 0 ? (
                        <div className="text-center py-5">
                            <FaShoppingCart size={40} className="text-muted mb-3" />
                            <p className="text-muted">{t('No pending sales found')}</p>
                        </div>
                    ) : (
                        <div style={{ padding: '20px' }}>
                            {/* Summary bar */}
                            <div style={{
                                borderRadius: '12px',
                                padding: '16px 20px',
                                marginBottom: '20px',
                                display: 'flex',
                                gap: '30px',
                                flexWrap: 'wrap'
                            }}>
                                <div>
                                    <div style={{ color: 'black', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{t('Total Due')}</div>
                                    <div style={{ color: '#ff6b6b', fontWeight: 700, fontSize: '20px' }}>
                                        {pendingSales.reduce((s, a) => s + parseFloat(a.due || 0), 0).toLocaleString()}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ color: 'black', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{t('Total Paid')}</div>
                                    <div style={{ color: '#4ade80', fontWeight: 700, fontSize: '20px' }}>
                                        {pendingSales.reduce((s, a) => s + parseFloat(a.paid || 0), 0).toLocaleString()}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ color: 'black', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{t('Sales')}</div>
                                    <div style={{ color: '#ffc107', fontWeight: 700, fontSize: '20px' }}>{pendingSales.length}</div>
                                </div>
                            </div>

                            {/* Table */}
                            <div>
                                <table className="table mb-0">
                                    <thead>
                                        <tr style={{ background: '#f1f5f9' }}>
                                            <th>{t('Sale #')}</th>
                                            <th>{t('Date')}</th>
                                            <th>{t('Products')}</th>
                                            <th>{t('Total')}</th>
                                            <th>{t('Paid')}</th>
                                            <th>{t('Due')}</th>
                                            <th>{t('Status')}</th>
                                            <th>{t('Action')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pendingSales.map((sale, idx) => (
                                            <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '14px 16px', fontWeight: 600, color: '#1e293b', border: 'none' }}>
                                                    <FaShoppingCart className="me-2" size={12} style={{ color: '#94a3b8' }} />
                                                    {sale.sale_number}
                                                </td>
                                                <td style={{ padding: '14px 16px', color: '#64748b', fontSize: '13px', border: 'none' }}>
                                                    {moment(sale.sale_date).format('DD MMM YYYY')}
                                                </td>
                                                <td style={{ padding: '14px 16px', fontSize: '12px', border: 'none' }}>
                                                    {sale.products && sale.products.length > 0 ? (
                                                        <div>
                                                            {sale.products.slice(0, 2).map((p, i) => (
                                                                <div key={i}>{p.product_name} (x{p.quantity})</div>
                                                            ))}
                                                            {sale.products.length > 2 && (
                                                                <small className="text-muted">+{sale.products.length - 2} more</small>
                                                            )}
                                                        </div>
                                                    ) : '-'}
                                                </td>
                                                <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: 600, color: '#1e293b', border: 'none' }}>
                                                    {parseFloat(sale.total).toLocaleString()}
                                                </td>
                                                <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: 600, color: '#16a34a', border: 'none' }}>
                                                    {parseFloat(sale.paid).toLocaleString()}
                                                </td>
                                                <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: 700, color: '#dc2626', border: 'none' }}>
                                                    {parseFloat(sale.due).toLocaleString()}
                                                </td>
                                                <td style={{ padding: '14px 16px', textAlign: 'center', border: 'none' }}>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        padding: '4px 12px',
                                                        borderRadius: '20px',
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        ...(sale.status === 'partial'
                                                            ? { background: '#fef3c7', color: '#d97706' }
                                                            : { background: '#fee2e2', color: '#dc2626' })
                                                    }}>
                                                        {sale.status === 'partial' ? t('Partial') : t('Unpaid')}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '14px 16px', textAlign: 'center', border: 'none' }}>
                                                    <Button
                                                        size="sm"
                                                        onClick={() => {
                                                            setShowPendingSales(false);
                                                            handleMakeCustomerPayment(sale);
                                                        }}

                                                        style={{
                                                            background: 'linear-gradient(135deg, #0f3460, #1a1a2e)',
                                                            border: 'none',
                                                            borderRadius: '8px',
                                                            fontSize: '12px',
                                                            fontWeight: 600,
                                                            padding: '6px 14px'
                                                        }}
                                                    >
                                                        <FaMoneyBillWave className="me-1" size={11} />
                                                        {t('Pay Now')}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer style={{ background: '#f8f9fa', borderTop: '1px solid #e2e8f0', padding: '12px 20px' }}>
                    <Button variant="secondary" size="sm" onClick={() => setShowPendingSales(false)}>
                        {t('Close')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default SalesTab;